import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { WorkspaceLayout, WorkspaceLayoutNode, WorkspacePane, WorkspaceSplit, TabState, SplitDirection } from '@arctek/sysml-types';
import type { LayoutController } from '../controllers/layout-controller.js';
import type { DocumentController } from '../controllers/document-controller.js';
import type { ModelerController } from '../controllers/modeler-controller.js';

@customElement('sysml-layout-renderer')
export class SysmlLayoutRenderer extends LitElement {
  static override readonly styles = css`
    :host {
      display: flex;
      flex: 1;
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      position: relative;
      overflow: hidden;
    }

    .layout-root {
      width: 100%;
      height: 100%;
      display: flex;
    }
  `;

  @property({ type: Object })
  layout?: WorkspaceLayout;

  @property({ type: Object })
  layoutController?: LayoutController;

  @property({ type: Object })
  documentController?: DocumentController;

  @property({ type: Object })
  modeler?: ModelerController;

  @property({ attribute: false })
  onSaveRequest?: () => void;

  @property({ type: String })
  theme: 'light' | 'dark' = 'light';

  private handleTabActivate(paneId: string, tabId: string) {
    const pane = this.findPane(this.layout!.root, paneId);
    if (!pane) return;

    const tab = pane.tabs.find(t => t.id === tabId);
    if (!tab || !tab.documentId) return;

    this.layoutController?.setActiveTab(paneId, tabId);

    // Load document into DocumentController
    this.documentController?.openDocument(tab.documentId, false);
    const doc = this.documentController?.documents.find(d => d.id === tab.documentId);
    if (doc) {
      this.modeler?.loadSourceText(doc.content);
    }
  }

  private handleTabClose(tabId: string) {
    this.layoutController?.closeTab(tabId);
  }

  private handleTabDrop(detail: { tabId: string; targetPaneId: string; index: number }) {
    this.layoutController?.moveTab(detail.tabId, detail.targetPaneId, detail.index);
  }

  private handleSplitResize(detail: { splitId: string; sizes: [number, number] }) {
    this.layoutController?.updateSplitSizes(detail.splitId, detail.sizes);
  }

  private handlePaneSplit(detail: { paneId: string; direction: SplitDirection }) {
    this.layoutController?.splitPane(detail.paneId, detail.direction);
  }

  private handlePaneClose(detail: { paneId: string }) {
    this.layoutController?.closePane(detail.paneId);
  }

  private handlePaneDrop(detail: { tabId: string; targetPaneId: string; zone: string }) {
    if (detail.zone === 'center') {
      // Simple move: drop tab into this pane's tab strip
      this.layoutController?.moveTab(detail.tabId, detail.targetPaneId);
    } else {
      // Edge drop: split the pane first, then move the tab into the new pane
      const directionMap: Record<string, SplitDirection> = {
        left: 'horizontal',
        right: 'horizontal',
        top: 'vertical',
        bottom: 'vertical',
      };
      const direction = directionMap[detail.zone];
      if (!direction) return;

      // Split the target pane
      const splitSuccess = this.layoutController?.splitPane(detail.targetPaneId, direction);
      if (!splitSuccess) return;

      // After split, find the new pane. For left/top drops, the tab should go to the
      // first child (original pane position); for right/bottom, to the second child (new pane).
      // The split creates: [original pane clone, new empty pane]
      // So for right/bottom, we move to the new pane (which is now activePaneId).
      // For left/top, we need to move the tab to the original and swap.
      const newActivePaneId = this.layoutController?.layout.activePaneId;
      if (!newActivePaneId) return;

      if (detail.zone === 'right' || detail.zone === 'bottom') {
        // New pane is already active (right/bottom), move tab there
        this.layoutController?.moveTab(detail.tabId, newActivePaneId);
      } else {
        // For left/top: the original content stays in first child, new empty pane is second child.
        // We want the dragged tab in the first position. Move tab to the new pane,
        // then the original content is already in the original position.
        this.layoutController?.moveTab(detail.tabId, newActivePaneId);
      }
    }
  }

  private findPane(node: WorkspaceLayoutNode, paneId: string): WorkspacePane | null {
    if (node.type === 'pane') {
      return node.id === paneId ? node : null;
    }
    return this.findPane(node.children[0], paneId) || this.findPane(node.children[1], paneId);
  }

  private countPanes(node: WorkspaceLayoutNode): number {
    if (node.type === 'pane') return 1;
    return this.countPanes(node.children[0]) + this.countPanes(node.children[1]);
  }

  private renderNode(node: WorkspaceLayoutNode): any {
    if (node.type === 'pane') {
      return this.renderPane(node);
    }
    return this.renderSplit(node);
  }

  private renderPane(pane: WorkspacePane): any {
    const isActive = this.layout?.activePaneId === pane.id;
    const isEmpty = pane.tabs.length === 0;
    const isSolePane = this.layout ? this.countPanes(this.layout.root) === 1 : true;

    const tabs = pane.tabs.map(tab => ({
      id: tab.id,
      label: tab.label,
      isDirty: tab.isDirty,
      isPinned: tab.isPinned,
      isActive: tab.id === pane.activeTabId,
    }));

    const activeTab = pane.tabs.find(t => t.id === pane.activeTabId);

    return html`
      <sysml-dock-pane
        .paneId=${pane.id}
        .isActive=${isActive}
        .isEmpty=${isEmpty}
        .isSolePane=${isSolePane}
        @click=${() => this.layoutController?.setActivePane(pane.id)}
        @paneSplit=${(e: CustomEvent) => this.handlePaneSplit(e.detail)}
        @paneClose=${(e: CustomEvent) => this.handlePaneClose(e.detail)}
        @paneDrop=${(e: CustomEvent) => this.handlePaneDrop(e.detail)}
      >
        <sysml-tab-strip
          slot="tab-strip"
          .paneId=${pane.id}
          .tabs=${tabs}
          @tabActivate=${(e: CustomEvent<string>) => this.handleTabActivate(pane.id, e.detail)}
          @tabClose=${(e: CustomEvent<string>) => this.handleTabClose(e.detail)}
          @tabDrop=${(e: CustomEvent) => this.handleTabDrop(e.detail)}
        ></sysml-tab-strip>

        ${!isEmpty && activeTab ? html`
          <div slot="body" style="width: 100%; height: 100%; display: flex; flex-direction: column;">
            ${this.renderTabContent(pane.id, activeTab)}
          </div>
        ` : nothing}
      </sysml-dock-pane>
    `;
  }

  private renderTabContent(paneId: string, tab: TabState): any {
    if (!tab.documentId) return nothing;

    if (tab.viewType === 'editor') {
      const doc = this.documentController?.documents.find(d => d.id === tab.documentId);
      if (!doc) return nothing;

      const monacoUri = `inmemory://sysml/document/${tab.documentId}`;

      return html`
        <sysml-editor
          .monacoUri=${monacoUri}
          .value=${doc.content}
          .theme=${this.theme}
          .externalDiagnostics=${this.modeler?.diagnostics ?? []}
          @valueChange=${(e: CustomEvent<string>) => {
            this.documentController?.updateDocumentContent(tab.documentId!, e.detail);
            if (this.layout?.activePaneId === paneId) {
              const editor = e.target as HTMLElement & { lastChangeRange?: { startLine: number; endLine: number; lineDelta: number } | null };
              this.modeler?.updateSourceText(e.detail, editor?.lastChangeRange ?? undefined);
            }
          }}
          @cursorChange=${(e: CustomEvent<{ line: number; column: number; offset: number }>) => {
            if (this.layout?.activePaneId === paneId) {
              this.modeler?.handleCursorChange(e.detail);
            }
          }}
          @saveRequest=${() => this.onSaveRequest?.()}
          style="flex: 1; width: 100%; height: 100%;"
        ></sysml-editor>
      `;
    }

    if (tab.viewType === 'diagram') {
      return html`
        <sysml-diagram
          .nodes=${this.modeler?.diagramNodes ?? []}
          .edges=${this.modeler?.diagramEdges ?? []}
          .partDefinitions=${this.modeler?.partDefinitions ?? []}
          .selectedNodeIds=${this.modeler?.selectedNodeIds ?? []}
          .selectedEdgeIds=${this.modeler?.selectedEdgeIds ?? []}
          .viewport=${this.modeler?.viewport}
          .theme=${this.theme}
          @nodeSelect=${(e: CustomEvent<string>) => this.modeler?.selectNode(e.detail)}
          @edgeSelect=${(e: CustomEvent<string>) => this.modeler?.selectEdge(e.detail)}
          @selectionClear=${() => this.modeler?.clearSelection()}
          style="flex: 1; width: 100%; height: 100%;"
        ></sysml-diagram>
      `;
    }

    return nothing;
  }

  private renderSplit(split: WorkspaceSplit): any {
    return html`
      <sysml-split-view
        .splitId=${split.id}
        .direction=${split.direction}
        .sizes=${split.sizes}
        @resize=${(e: CustomEvent) => this.handleSplitResize(e.detail)}
      >
        <div slot="first" style="width: 100%; height: 100%; display: flex;">
          ${this.renderNode(split.children[0])}
        </div>
        <div slot="second" style="width: 100%; height: 100%; display: flex;">
          ${this.renderNode(split.children[1])}
        </div>
      </sysml-split-view>
    `;
  }

  override render() {
    if (!this.layout) {
      return html`<div class="layout-root">Loading layout...</div>`;
    }

    return html`
      <div class="layout-root">
        ${this.renderNode(this.layout.root)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-layout-renderer': SysmlLayoutRenderer;
  }
}
